import { processListingParams } from '@/api/helpers';
import { getExceptionFromAxiosError } from '@/client/helpers';
import { AxiosError, AxiosInstance, AxiosResponse } from 'axios';
import {
  CampaignMasterTemplate,
  CampaignMasterTemplatesListParams,
  CampaignMasterTemplatesListResponse,
  CreateCampaignMasterTemplateRequest,
  PhishingTranslationStatusResponse,
  TranslatePhishingTemplateRequest,
  TranslatePhishingTemplateResponse,
  UpdateCampaignMasterTemplateRequest,
} from './types';

export const masterTemplatesService = (client: AxiosInstance) => {
  const BASE_URL = '/gophish/campaign-master-templates';

  const getMasterTemplates = async (
    params?: CampaignMasterTemplatesListParams,
  ): Promise<CampaignMasterTemplatesListResponse> => {
    try {
      const result = await client.get<
        CampaignMasterTemplatesListResponse,
        AxiosResponse<CampaignMasterTemplatesListResponse>
      >(BASE_URL, {
        withCredentials: true,
        params: params ? processListingParams(params) : undefined,
      });

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const getMasterTemplate = async (
    id: string,
  ): Promise<CampaignMasterTemplate> => {
    try {
      const result = await client.get<
        CampaignMasterTemplate,
        AxiosResponse<CampaignMasterTemplate>
      >(`${BASE_URL}/${id}`, {
        withCredentials: true,
      });

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const createMasterTemplate = async (
    request: CreateCampaignMasterTemplateRequest,
  ): Promise<CampaignMasterTemplate> => {
    try {
      const result = await client.post<
        CampaignMasterTemplate,
        AxiosResponse<CampaignMasterTemplate>
      >(BASE_URL, request, {
        withCredentials: true,
      });

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const updateMasterTemplate = async (
    id: string,
    request: UpdateCampaignMasterTemplateRequest,
  ): Promise<CampaignMasterTemplate> => {
    try {
      const result = await client.patch<
        CampaignMasterTemplate,
        AxiosResponse<CampaignMasterTemplate>
      >(`${BASE_URL}/${id}`, request, {
        withCredentials: true,
      });

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const deleteMasterTemplate = async (id: string): Promise<void> => {
    try {
      await client.delete(`${BASE_URL}/${id}`, {
        withCredentials: true,
      });
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const disassociateTemplate = async (
    masterTemplateId: string,
    templateId: string,
  ): Promise<void> => {
    try {
      await client.delete(
        `${BASE_URL}/${masterTemplateId}/templates/${templateId}`,
        { withCredentials: true },
      );
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const setPrimaryTemplate = async (
    masterTemplateId: string,
    templateId: string,
  ): Promise<void> => {
    try {
      await client.patch(
        `${BASE_URL}/${masterTemplateId}/primary-template`,
        { templateId },
        { withCredentials: true },
      );
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const translatePhishingTemplate = async (
    masterTemplateId: string,
    request: TranslatePhishingTemplateRequest,
  ): Promise<TranslatePhishingTemplateResponse> => {
    try {
      const result = await client.post<
        TranslatePhishingTemplateResponse,
        AxiosResponse<TranslatePhishingTemplateResponse>
      >(`${BASE_URL}/${masterTemplateId}/translate`, request, {
        withCredentials: true,
      });

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const getPhishingTranslationStatus = async (
    masterTemplateId: string,
  ): Promise<PhishingTranslationStatusResponse> => {
    try {
      const result = await client.get<
        PhishingTranslationStatusResponse,
        AxiosResponse<PhishingTranslationStatusResponse>
      >(`${BASE_URL}/${masterTemplateId}/translate/status`, {
        withCredentials: true,
      });

      return result.data;
    } catch (e) {
      throw getExceptionFromAxiosError(e as AxiosError);
    }
  };

  const phishingTranslationNotifyUrl = (masterTemplateId: string): string => {
    return `${client.defaults.baseURL}gophish/campaign-master-templates/${masterTemplateId}/translate/notify`;
  };

  return {
    getMasterTemplates,
    getMasterTemplate,
    createMasterTemplate,
    updateMasterTemplate,
    deleteMasterTemplate,
    disassociateTemplate,
    setPrimaryTemplate,
    translatePhishingTemplate,
    getPhishingTranslationStatus,
    phishingTranslationNotifyUrl,
  };
};
